import React, { memo } from 'react';
import PropTypes from 'prop-types';
import MenuItem from '@material-ui/core/MenuItem';
import TextField from '@material-ui/core/TextField';
import { useTheme, withStyles } from '@material-ui/core/styles';
import classNames from 'classnames';

import caretOrange from '../../assets/svg/caretOrange.svg';
import caretGray from '../../assets/svg/caretGray.svg';
import calendar from '../../assets/svg/calendar.svg';
import styles from './select.styles';
import { ElemType, MapClassesToElem } from '../../helpers/styles-helpers';
import { fieldLabel } from '../../helpers/sentence-case';

const nativeIcons = {
  caretOrange,
  caretGray,
  calendar
};

const emptyLabelClasses = {
  light: 'empty'
};

const StyledSelect = memo(
  ({ classes, mode, icon, options, onChange, showDefaultValue, truncateSelectedValue = false, ...props }) => {
    const modeIcon = mode === 'dark' ? nativeIcons.caretOrange : nativeIcons.caretGray;
    const expandIcon = ({ className }) => (
      <img src={icon ? nativeIcons[icon] : modeIcon} alt="select" className={classNames(className, classes.caret)} />
    );
    const selectOptions = options || [];
    // Pull InputProps out of the spread so a caller-supplied endAdornment
    // (e.g. the tooltip-icon adornment from the select field type, #30648)
    // is preserved while the wrapper's mode-specific `classes` stays
    // authoritative — see the merge order in InputProps below.
    const { icons, InputProps: callerInputProps, ...fieldProps } = props;
    const { value } = fieldProps;
    const classPrefix = mode || 'default';
    const theme = useTheme();

    return (
      <TextField
        select
        SelectProps={{
          disableUnderline: true,
          displayEmpty: true,
          classes: mode ? MapClassesToElem(`${mode}-${ElemType.TextField}`, classes) : null,
          IconComponent: expandIcon,
          renderValue: (value) => {
            const selected = selectOptions.find((option) => String(option.id) === String(value));
            const displayValue = selected?.value !== undefined && selected?.value !== null ? selected.value : value;

            // Apply truncation only when explicitly requested
            if (truncateSelectedValue && displayValue.length > 25) {
              return displayValue.slice(0, 25) + '...';
            }

            return displayValue;
          }
        }}
        InputLabelProps={{
          classes: MapClassesToElem(`${classPrefix}-${ElemType.Label}`, classes),
          className: classNames({
            [classes[emptyLabelClasses[mode]]]: value === null && emptyLabelClasses[mode]
          }),
          shrink: value !== null
        }}
        FormHelperTextProps={{ classes: MapClassesToElem(`${classPrefix}-${ElemType.FormHelper}`, classes) }}
        onChange={(e) => onChange(e.target.name, e.target.value)}
        {...fieldProps}
        label={fieldLabel(theme, fieldProps.label)}
        InputProps={{
          ...(callerInputProps || {}),
          // Set last so the wrapper's mode-driven classes stay authoritative
          // and can't be silently clobbered by a caller passing their own
          // InputProps.classes.
          classes: mode ? MapClassesToElem(`${mode}-${ElemType.Input}`, classes) : null
        }}
      >
        {selectOptions.map((option) => (
          <MenuItem
            classes={{ selected: classes.selected, root: classes.select }}
            key={option.id}
            value={option.id}
            disabled={option.disabled}
          >
            {icons && icons[option.id] ? (
              <>
                <img className={classes.selectIcon} alt={option.id} src={icons[option.id]} />
                {option.value} {showDefaultValue && option.isDefault && '(Default)'}
              </>
            ) : (
              `${option.value} ${showDefaultValue && option.isDefault ? '(Default)' : ''}`
            )}
          </MenuItem>
        ))}
      </TextField>
    );
  }
);

StyledSelect.propTypes = {
  onChange: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(PropTypes.object).isRequired,
  classes: PropTypes.object.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  mode: PropTypes.oneOf([null, 'light', 'dark']),
  className: PropTypes.string,
  icons: PropTypes.object,
  icon: PropTypes.oneOf([null, 'calendar', 'caretOrange', 'caretGray']),
  showDefaultValue: PropTypes.bool
};

StyledSelect.defaultProps = {
  mode: null,
  className: 'home',
  icons: undefined,
  icon: null,
  value: '',
  showDefaultValue: false
};

export default withStyles(styles)(StyledSelect);
